import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  getLiveMatchState,
  getRoundHistory,
  getPlayerStats,
  MatchLiveState,
  RoundHistory,
  PlayerMatchStats,
  calculatePlayerRating,
  calculateADR,
  calculateKD,
  calculateKASTPercent
} from '../services/matchLiveService';
import { baseURLnoapi } from '../Constants';

// ========================================
// INTERFACES
// ========================================

export interface LiveMatchContextData {
  // Match state
  liveState: MatchLiveState | null;
  roundHistory: RoundHistory[];
  playerStats: PlayerMatchStats[];

  // Status
  isLive: boolean;
  isLoading: boolean;
  error: string | null;

  // Post-match indicators
  isPostMatch: boolean; // Match ended, showing preliminary stats
  hasDetailedStats: boolean; // Final stats available from backend

  // Polling control
  lastUpdated: Date | null;

  // Actions
  refresh: () => Promise<void>;
}

const LiveMatchContext = createContext<LiveMatchContextData | undefined>(undefined);

// ========================================
// PROVIDER COMPONENT
// ========================================

interface LiveMatchProviderProps {
  children: ReactNode;
  matchId: string;
  onScoreUpdate?: (data: {
    team1Score: number;
    team2Score: number;
    currentMap?: string;
    currentRound?: number;
    maxRounds?: number;
  }) => void;
  onDetailedStatsReady?: () => void;
}

export const LiveMatchWebhookProvider: React.FC<LiveMatchProviderProps> = ({ children, matchId, onScoreUpdate, onDetailedStatsReady }) => {
  const [liveState, setLiveState] = useState<MatchLiveState | null>(null);
  const [roundHistory, setRoundHistory] = useState<RoundHistory[]>([]);
  const [playerStats, setPlayerStats] = useState<PlayerMatchStats[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [isPostMatch, setIsPostMatch] = useState(false);
  const [hasDetailedStats, setHasDetailedStats] = useState(false);

  // Notify parent component when live state updates
  useEffect(() => {
    if (liveState && onScoreUpdate) {
      const updateData = {
        team1Score: liveState.team1_score || 0,
        team2Score: liveState.team2_score || 0,
        currentMap: liveState.map_name || '',
        currentRound: liveState.round_number || 1,
        maxRounds: 24
      };
      console.log('[LiveMatch] Calling onScoreUpdate:', updateData);
      onScoreUpdate(updateData);
    }
  }, [liveState?.team1_score, liveState?.team2_score, liveState?.map_name, liveState?.round_number, onScoreUpdate]);

  // ========================================
  // FETCH DATA FROM WEBHOOK SYSTEM
  // ========================================

  const fetchLiveData = useCallback(async () => {
    try {
      setError(null);

      console.log('[LiveMatch] Fetching live data for match:', matchId);

      // Fetch all data in parallel
      const [stateData, roundsData, statsData] = await Promise.all([
        getLiveMatchState(matchId),
        getRoundHistory(matchId),
        getPlayerStats(matchId)
      ]);

      console.log('[LiveMatch] API responses:', {
        stateData,
        roundsData,
        statsData
      });

      setLiveState(stateData);
      setRoundHistory(roundsData);
      setPlayerStats(statsData);
      setLastUpdated(new Date());

      // Determine if match is live or finished
      if (stateData) {
        const matchFinished = stateData.status === 'finished' || stateData.status === 'completed';
        setIsPostMatch(matchFinished);

        // If match just finished, we're showing preliminary live stats
        // Detailed stats will come later
        if (matchFinished && !hasDetailedStats) {
          console.log('Match ended - showing preliminary live stats');
        }
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching live match data:', err);
      setError('Failed to load live match data');
      setIsLoading(false);
    }
  }, [matchId, hasDetailedStats]);

  // ========================================
  // CHECK FOR DETAILED STATS (POST-MATCH)
  // ========================================

  const checkForDetailedStats = useCallback(async () => {
    try {
      console.log('[LiveMatch] Checking Rising Power Stats API for detailed stats...');

      // Use the same API endpoint that MatchPage uses
      const response = await fetch(
        `https://api.rising.powerstats.dk/RisingStats/api/Match/${matchId}`
      );

      if (response.ok) {
        const data = await response.json();

        // If the API returns data, the match has detailed stats
        if (data && Object.keys(data).length > 0) {
          console.log('[LiveMatch] ✅ Detailed stats found! Notifying parent...');
          setHasDetailedStats(true);

          // Notify parent component that detailed stats are ready
          if (onDetailedStatsReady) {
            onDetailedStatsReady();
          }
        } else {
          console.log('[LiveMatch] ⏳ Detailed stats not ready yet');
        }
      } else {
        console.log('[LiveMatch] ⏳ Detailed stats not ready yet (404 or error)');
      }
    } catch (err) {
      console.log('[LiveMatch] ⏳ Detailed stats not ready yet (fetch error)');
    }
  }, [matchId, onDetailedStatsReady]);

  // ========================================
  // SSE CONNECTION FOR LIVE UPDATES
  // ========================================

  useEffect(() => {
    // Initial fetch
    fetchLiveData();

    let eventSource: EventSource | null = null;
    let pollInterval: NodeJS.Timeout | null = null;

    if (isPostMatch && !hasDetailedStats) {
      // Match ended, poll every 60 seconds for detailed stats from Rising backend
      console.log('[LiveMatch] Post-match mode: polling every 60s for detailed stats');
      pollInterval = setInterval(() => {
        checkForDetailedStats();
      }, 60000); // 60 seconds
    } else if (!isPostMatch) {
      // Match is live, use SSE for real-time updates
      console.log('[LiveMatch] Live mode: connecting to SSE for webhook updates');

      // SSE endpoint is public (no authentication required)
      const sseUrl = `${baseURLnoapi}/api/match/${matchId}/live/stream`;

      eventSource = new EventSource(sseUrl);

      eventSource.onmessage = (event) => {
        console.log('[LiveMatch SSE] Received update:', event.data);

        // Parse the SSE event
        try {
          const data = JSON.parse(event.data);

          // Check event type (handle both lowercase and PascalCase from backend)
          const eventType = data.type || data.Type;

          // Fetch fresh data on any meaningful event (ignore heartbeat and connected)
          if (eventType && eventType !== 'heartbeat' && eventType !== 'connected') {
            console.log('[LiveMatch SSE] Fetching fresh data after webhook:', eventType);
            fetchLiveData();
          }
        } catch (err) {
          console.error('[LiveMatch SSE] Failed to parse event data:', err);
        }
      };

      eventSource.onerror = (error) => {
        console.error('[LiveMatch SSE] Connection error:', error);

        // Close and attempt reconnect after 5 seconds
        eventSource?.close();
        setTimeout(() => {
          console.log('[LiveMatch SSE] Attempting reconnection...');
          fetchLiveData(); // Fetch latest data
        }, 5000);
      };

      eventSource.onopen = () => {
        console.log('[LiveMatch SSE] Connection established');
      };
    }

    return () => {
      // Cleanup
      if (eventSource) {
        console.log('[LiveMatch SSE] Closing connection');
        eventSource.close();
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [matchId, isPostMatch, hasDetailedStats, fetchLiveData, checkForDetailedStats]);

  // ========================================
  // CONTEXT VALUE
  // ========================================

  const contextValue: LiveMatchContextData = {
    liveState,
    roundHistory,
    playerStats,

    isLive: liveState?.status === 'live',
    isLoading,
    error,

    isPostMatch,
    hasDetailedStats,

    lastUpdated,

    refresh: fetchLiveData
  };

  return (
    <LiveMatchContext.Provider value={contextValue}>
      {children}
    </LiveMatchContext.Provider>
  );
};

// ========================================
// CUSTOM HOOK
// ========================================

export const useLiveMatchWebhook = (): LiveMatchContextData => {
  const context = useContext(LiveMatchContext);
  if (context === undefined) {
    throw new Error('useLiveMatchWebhook must be used within a LiveMatchWebhookProvider');
  }
  return context;
};

// ========================================
// HELPER FUNCTIONS (exported for components)
// ========================================

/**
 * Get enriched player stats with calculated fields
 */
export const getEnrichedPlayerStats = (stats: PlayerMatchStats[]) => {
  return stats.map(player => ({
    ...player,
    rating: calculatePlayerRating(player),
    adr: calculateADR(player),
    kd: calculateKD(player),
    kastPercent: calculateKASTPercent(player)
  }));
};

/**
 * Get players sorted by rating (for scoreboard)
 */
export const getPlayersByRating = (stats: PlayerMatchStats[]) => {
  const enriched = getEnrichedPlayerStats(stats);
  return enriched.sort((a, b) => b.rating - a.rating);
};

/**
 * Get players by team
 */
export const getPlayersByTeam = (stats: PlayerMatchStats[], teamId: number) => {
  return stats.filter(player => player.team_id === teamId);
};

/**
 * Get recent rounds (last N rounds)
 */
export const getRecentRounds = (rounds: RoundHistory[], count: number = 10) => {
  return rounds
    .sort((a, b) => b.round_number - a.round_number)
    .slice(0, count)
    .reverse();
};

/**
 * Get current map info from live state
 */
export const getCurrentMapInfo = (liveState: MatchLiveState | null) => {
  if (!liveState) return null;

  return {
    mapNumber: liveState.map_number,
    mapName: liveState.map_name || `Map ${liveState.map_number + 1}`,
    roundNumber: liveState.round_number,
    team1Score: liveState.team1_score,
    team2Score: liveState.team2_score,
    team1Side: liveState.team1_side || 'UNKNOWN',
    team2Side: liveState.team2_side || 'UNKNOWN'
  };
};

/**
 * Get series score (map wins)
 */
export const getSeriesScore = (liveState: MatchLiveState | null) => {
  if (!liveState) return { team1: 0, team2: 0 };

  return {
    team1: liveState.team1_series_score || 0,
    team2: liveState.team2_series_score || 0
  };
};
